import { useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  usePropertyField,
  useGallery,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// Page-specific fork of HeroSplitBG for the Pet Policy page. Identical to that
// hero in every respect except one: the CTA link target resolves to the
// property's `custom_pet_screening_url` when present, falling back to the
// literal `ctaUrl` (the public PetScreening app) otherwise. This lives in its
// own slot so the shared HeroSplitBG used by other pages stays untouched.
interface PetPolicyHeroProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
  ctaLabel?: string;
  /** Fallback CTA URL used when the property has no `ctaUrlField` value. */
  ctaUrl?: string;
  /**
   * Property field (typically `custom_pet_screening_url`) whose value, when
   * non-empty for the property, overrides `ctaUrl` for the CTA target.
   */
  ctaUrlField?: string;
  altText?: string;
  bgImageField?: string;
  bgImageUrl?: string;
  bgAltField?: string;
}

const BG_IMAGE_DEF: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Hero background' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function PetPolicyHero({
  tokenPrefix,
  title = 'Pet Policy',
  blurbField,
  defaultText,
  ctaLabel = 'Learn More',
  ctaUrl = 'https://app.petscreening.com',
  ctaUrlField = 'custom_pet_screening_url',
  altText = 'Hero background',
  bgImageField,
  bgImageUrl,
  bgAltField,
}: PetPolicyHeroProps) {
  const prefix = tokenPrefix || 'petpolicyhero';
  const titlePath = `${prefix}.title`;
  const blurbPath = `${prefix}.blurb`;
  const ctaPath = `${prefix}.cta_link`;
  const bgPath = `${prefix}.bg_image`;

  // Resolve the CTA target: the property's custom pet-screening URL when set,
  // otherwise the literal fallback. Always call the hook (empty key when no
  // field is configured) so hook order stays stable; an absent key resolves to
  // `undefined`.
  const ctaFieldUrl = usePropertyField(ctaUrlField || '');
  const resolvedCtaUrl =
    (typeof ctaFieldUrl === 'string' && ctaFieldUrl.trim()) || ctaUrl;

  const ctaLinkDef: SlotGroupDefinition = {
    label: 'CTA Link',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: resolvedCtaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Link Text',
        type: 'text',
        defaultValue: ctaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  const bgImageDef: SlotGroupDefinition = {
    label: 'Background Image',
    fields: {
      image: bgImageUrl
        ? {
            label: 'Image',
            type: 'image',
            defaultValue: bgImageUrl,
          }
        : {
            label: 'Image',
            type: 'image',
            defaultField: bgImageField || 'property_featured_image',
          },
      alt: bgAltField
        ? { label: 'Alt Text', type: 'text', defaultField: bgAltField }
        : { label: 'Alt Text', type: 'text', defaultValue: altText },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: 0,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };

  const cta = useSlotGroup(ctaPath, ctaLinkDef);
  const bgImage = useSlotGroup(bgPath, bgImageDef);

  // Graceful, error-aware background fallback chain: page-relevant default →
  // property featured image → first usable gallery photo.
  const featuredImage = usePropertyField('property_featured_image');
  const gallery = useGallery() as Array<{ image?: string | null; original_url?: string | null }>;
  const galleryFallback = useMemo(() => {
    for (const photo of gallery) {
      const src = photo?.image || photo?.original_url;
      if (src) return src;
    }
    return null;
  }, [gallery]);

  const candidates = useMemo(
    () =>
      [bgImage.image, featuredImage, galleryFallback].filter(
        (url): url is string => Boolean(url),
      ),
    [bgImage.image, featuredImage, galleryFallback],
  );
  const candidatesKey = candidates.join('|');
  const [candidateIdx, setCandidateIdx] = useState(0);
  useEffect(() => {
    setCandidateIdx(0);
  }, [candidatesKey]);
  const displayImage = candidates[candidateIdx] || null;

  const imageAlt = useMemo(() => bgImage.alt || altText, [bgImage.alt, altText]);

  return (
    <section
      className="relative flex items-center overflow-hidden text-white min-h-[60vh] min-h-[480px] pb-14 md:pb-20"
      style={{
        paddingTop: 'calc(var(--noh-banner-height, 0px) + var(--offer-banner-height, 0px) + 5rem)',
        transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <EditableSlotGroup
        groupPath={bgPath}
        definition={bgImageDef}
        className="absolute inset-0"
      >
        {displayImage ? (
          <VibesitesImage
            key={displayImage}
            url={displayImage}
            alt={imageAlt}
            priority
            className="absolute inset-0 h-full w-full object-cover"
            onError={() =>
              setCandidateIdx((i) => (i < candidates.length - 1 ? i + 1 : i))
            }
          />
        ) : (
          <div className="absolute inset-0 bg-primary" />
        )}
        <div className="absolute inset-0 bg-black/40" />
      </EditableSlotGroup>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
        <div className="hero-split-content flex flex-col gap-6 items-start w-full max-w-xl">

          {/* Page Title */}
          <div className="min-w-0 w-full">
            <RichTextContent
              path={titlePath}
              defaultValue={`<p>${title}</p>`}
              as="h1"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-wider leading-tight break-words text-left drop-shadow-lg"
            />
          </div>

          {/* Small white divider beneath the title */}
          <div
            aria-hidden="true"
            className="h-0.5 w-12 bg-white/80 drop-shadow"
          />

          {/* Blurb + CTA */}
          <RichTextContent
            path={blurbPath}
            defaultField={blurbField}
            defaultValue={defaultText || ''}
            as="div"
            className="font-body text-base md:text-lg leading-relaxed opacity-95 drop-shadow [&_p]:mb-5 [&_p:last-child]:mb-0 [&_a]:underline text-left"
          />
          <div className="text-left">
            <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
              <a
                href={cta.url || '#'}
                target={cta._target || '_self'}
                className="hero-split-cta-btn group/cta relative inline-flex items-center gap-2 px-8 py-3 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{cta.label || ctaLabel}</span>
              </a>
            </EditableSlotGroup>
          </div>

        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="petpolicyhero.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="petpolicyhero.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="petpolicyhero.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="petpolicyhero.bg_image"
        definition={BG_IMAGE_DEF}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .hero-split-content {
          animation: heroSplitFadeIn 0.9s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes heroSplitFadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-split-content { animation: none; }
        }
        .hero-split-cta-btn {
          background: transparent;
          border: 1.5px solid color-mix(in srgb, #fff 75%, transparent);
          border-radius: 2px;
          color: #fff;
          box-shadow: none;
        }
        .hero-split-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: color-mix(in srgb, #fff 18%, transparent);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .hero-split-cta-btn:hover {
          border-color: #fff;
          color: #fff;
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.25),
            0 4px 16px color-mix(in srgb, #fff 18%, transparent);
        }
        .hero-split-cta-btn:hover::before {
          opacity: 1;
        }
        .hero-split-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicyHero',
  displayName: 'Hero – Pet Policy',
  tokens: {
    'petpolicyhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Pet Policy</p>',
    },
    'petpolicyhero.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'petpolicyhero.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: 'https://app.petscreening.com',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'Learn More',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
    'petpolicyhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Hero background' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(PetPolicyHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicyHero;
